import { getLinkAgentIds } from '#~/agent/components/bridge/hooks.js';
import { fetchRequestLink, linkAgent, listAgents, listRooms } from '#~/api/agent/registry.js';
import type { LinkRequest } from '#~/api/agent/types.js';
import { ApiError as ApiErrorClass } from '#~/api/types.js';
import { createBridgeLifecycleTracker, type AnalyticsTrackFn } from '#~/analytics/domains/index.js';
import { track as trackAnalytics } from '#~/analytics/track.js';
import { EVENT_SEVERITY } from '#~/analytics/constants/index.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { toHexFromId } from '#~/api/utils.js';
import { classifyFetchError } from '#~/boot/classify-error.js';
import { config } from '#~/config/index.js';
// ENG-4154: the controller observes IdentityState store transitions; the
// `force` pull maps to the store's pull primitive (W1 §1 #9). The live
// consult bypasses the store's session-sticky caches entirely (see
// reconfirmSidecarLinkLive).
import { consultSidecarLinkLive, resolveLinkedAgentId } from '#~/electron/identity-store.js';
import { ElectronAPI } from '#~/electron/api.js';
import { isElectronApp } from '#~/electron/utils.js';
import * as persistenceLayer from '#~/persistence/index.js';
import { timeBootHop, withBootProgressHeartbeat } from '#~/logger/forward.js';
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { StatusCode } from 'status-code-enum';
import { waitForBridgeLinked } from './auto-link-bridge-monitor.js';
import {
  computeLinkRetryBackoffMs,
  isAtobDomException,
  prewarmMailroomHttpEndpoint
} from './auto-link-prewarm.js';
import { primeMachineAgentCache } from './auto-link-machine-agent-cache.js';
import {
  clearAutoLinkCreateIdempotencyKey,
  resolveAutoLinkCreateIdempotencyKey
} from './auto-link-idempotency.js';
import {
  checkBridgeLinkedRoom,
  checkRoomAccessible,
  createAutoLinkRoom,
  deleteAutoLinkRoom
} from './auto-link-room.js';
import type { RoomAccess, RoomCheckResult, RoomCreationResult } from './auto-link-room.js';
// Per ENG-4034 (see lib/with-timeout.ts): network awaits in boot gates must
// carry a client-side deadline or a hung request wedges the gate forever.
import { withTimeout } from '#~/lib/with-timeout.js';
import { resolveAutoLinkStudioName } from './auto-link-studio-name.js';
import type {
  AutoLinkContext,
  AutoLinkControllerOptions,
  AutoLinkState
} from './auto-link-types.js';
import { evaluateAutoLinkVendor } from './auto-link-vendor.js';
import {
  AUTO_LINK_VERSION,
  DEFAULT_BRIDGE_ONLINE_TIMEOUT_MS,
  DEFAULT_IPC_TIMEOUT_MS,
  DEFAULT_SERVER_CHECK_TIMEOUT_MS,
  DEFAULT_SIDECAR_RECONFIRM_GRACE_MS,
  DEFAULT_STALE_REVALIDATE_GRACE_MS,
  PERSISTENCE_KEY_COMPLETED
} from './auto-link-types.js';

type StateSubscriber = (state: AutoLinkState) => void;
type LinkAgentIdsResult = Awaited<ReturnType<typeof getLinkAgentIds>>;
type AutoLinkRequestId = NonNullable<LinkAgentIdsResult['requestId']>;
type CompletionRecordInspection = 'absent' | 'stale' | 'valid';

let controllerInstance: AutoLinkController | undefined;

interface CompletionRecord {
  readonly completedAt: number;
  readonly roomId: string;
  readonly userId: string;
  readonly version: string;
}

interface AttemptEntry {
  readonly timestamp: number;
  readonly userId: string;
}

interface AttemptRecord {
  readonly attempts: AttemptEntry[];
}

interface RunContext {
  roomId: Room['id'] | undefined;
  roomCreated: boolean;
  linkSucceeded: boolean;
  authoritativeRoom?: Extract<RoomCreationResult, { readonly status: 'room' }>['authoritativeRoom'];
  authoritativeOwnedOrganizations?: Extract<
    RoomCreationResult,
    { readonly status: 'room' }
  >['authoritativeOwnedOrganizations'];
}

interface VerifiedCompletionOptions {
  readonly bridgeId: Agent['id'];
  readonly roomId: Room['id'];
  readonly successReason:
    | 'already_linked'
    | 'candidate_already_linked'
    | 'conflict_recovered'
    | undefined;
  readonly trackSuccess: boolean;
  readonly authoritativeRoom?: Extract<
    RoomCreationResult,
    { readonly status: 'room' }
  >['authoritativeRoom'];
  readonly authoritativeOwnedOrganizations?: Extract<
    RoomCreationResult,
    { readonly status: 'room' }
  >['authoritativeOwnedOrganizations'];
}

interface RunCompletionLifecycle {
  readonly accountId: string;
  clearStarted: boolean;
  requestId: AutoLinkRequestId | undefined;
  status: 'pending' | 'verified' | 'closed';
}

type AutoLinkPhase = 'checking' | 'linking' | 'verifying_link';

const ABORT_ERROR_NAME = 'AbortError';
const ABORT_ERROR_MESSAGE = 'Auto-link was cancelled';

class MalformedStudioInventoryError extends Error {
  override readonly name = 'MalformedStudioInventoryError';

  constructor() {
    super('Studio inventory contains a room with a malformed identity');
  }
}
// ENG-4166 D2: message for the cross-account terminal — the bridge is linked to
// a room the current token cannot access and the renderer cannot clear the link
// in place (bridge:unlink reboots, and bridge:reboot is suppressed under
// IRIS_E2E_TEST). Never a false `success`; root bootstrap routes this to the
// boot-error screen (D3).
const CROSS_ACCOUNT_LINK_MESSAGE = 'Bridge is linked to a studio this account cannot access';
const PERSISTENCE_WRITE_RETRIES = 3;
const PERSISTENCE_KEY_ATTEMPTS = 'auto_link_v2_attempts';
const MAX_ATTEMPTS_PER_WINDOW = 3;
const ATTEMPT_WINDOW_MS = 5 * 60_000;
const ATTEMPT_RETENTION_MS = 10 * 60_000;
// linkAgent retry budget for transient cloud-NATS failures during fresh-boot
// auto-link. See the retry loop in runStateMachine for context.
const MAX_LINK_ATTEMPTS = 3;
const LINK_RETRY_BACKOFF_BASE_MS = 2_000;
// S8 (F2 completeness, audit GAP1): a linkAgent attempt participant-waits ~29s
// on the cloud and waitForBridgeLinked can poll for many seconds — both with no
// intermediate auto-link state transition, so the F2 watchdog's progress clock
// would go stale mid-wait and reboot a healthy-but-slow link. Tick the clock
// every BOOT_PROGRESS_HEARTBEAT_MS (well under the 10s/17s watchdog windows)
// only WHILE such a bounded cloud call is in flight (see withBootProgressHeartbeat).
const BOOT_PROGRESS_HEARTBEAT_MS = 5_000;
// Product skip rule — UNCHANGED from before this ticket. Matching the error
// text is what the checking phase already used to decide `skipped`. Do not
// tighten this to navigator.onLine: that would turn a fetch failure into
// an on-screen error and hold the overlay. Telemetry-only work must not
// change that path. Per stay-in-scope (ai.knowledge/global).
const isNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();

  return (
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    message.includes('offline') ||
    message.includes('err_network') ||
    (error.name === 'TypeError' && message.includes('fetch'))
  );
};

// Telemetry-only: cleanup vs failure. A skipped fetch while the laptop is
// still online stays a real failure on the event so a blocked request cannot
// hide in the cleanup bucket. The product skip above is untouched.
// Same offline bit boot already uses (classifyFetchError).
const isOffline = (error: unknown): boolean => classifyFetchError(error) === 'offline';

const LINK_CONFLICT_MESSAGE_PATTERNS = [
  'already linked',
  'already associated',
  'already connected',
  'link already exists',
  'agent already linked',
  'bridge already linked'
];

const analyticsTrack: AnalyticsTrackFn = (name, category, properties) => {
  void trackAnalytics({
    name,
    category,
    properties
  } as AnalyticsEvent);
};

const autoLinkAnalytics = createBridgeLifecycleTracker(analyticsTrack);

const withOptionalRequestId = (requestId?: AutoLinkRequestId): { request_id?: string } =>
  requestId ? { request_id: requestId } : {};

const withOptionalAgentId = (agentId?: Agent['id']): { agent_id?: Agent['id'] } =>
  agentId ? { agent_id: agentId } : {};

export class AutoLinkController {
  private state: AutoLinkState = { status: 'idle' };
  private abortController: AbortController | undefined;
  // Boot-hop diagnostics: wall-clock start of the state machine run, so the
  // terminal transition can emit an autoLink.total IRIS_DIAG_BOOT_HOP line
  // covering the whole checking→terminal span (see logger/forward.ts).
  private machineStartedAt: number | undefined;
  private readonly subscribers: Set<StateSubscriber> = new Set();
  private readonly options: Required<AutoLinkControllerOptions>;

  private constructor(options: AutoLinkControllerOptions = {}) {
    this.options = {
      ipcTimeoutMs: options.ipcTimeoutMs ?? DEFAULT_IPC_TIMEOUT_MS,
      bridgeOnlineTimeoutMs: options.bridgeOnlineTimeoutMs ?? DEFAULT_BRIDGE_ONLINE_TIMEOUT_MS,
      serverCheckTimeoutMs: options.serverCheckTimeoutMs ?? DEFAULT_SERVER_CHECK_TIMEOUT_MS,
      sidecarReconfirmGraceMs:
        options.sidecarReconfirmGraceMs ?? DEFAULT_SIDECAR_RECONFIRM_GRACE_MS,
      staleRevalidateGraceMs: options.staleRevalidateGraceMs ?? DEFAULT_STALE_REVALIDATE_GRACE_MS,
      onStateChange: options.onStateChange ?? (() => {}),
      onNonFatalError: options.onNonFatalError ?? (() => {})
    };
  }

  static getInstance(options?: AutoLinkControllerOptions): AutoLinkController {
    if (!controllerInstance) {
      controllerInstance = new AutoLinkController(options);
    } else if (options !== undefined && process.env.NODE_ENV === 'development') {
      console.warn(
        '#[auto-link] Options ignored - controller already initialized. Use resetInstance() first if reconfiguration needed.'
      );
    }
    return controllerInstance;
  }

  static resetInstance(): void {
    if (controllerInstance) {
      controllerInstance.abort();
      controllerInstance = undefined;
    }
  }

  static peekInstance(): AutoLinkController | undefined {
    return controllerInstance;
  }

  getState(): AutoLinkState {
    return this.state;
  }

  subscribe(fn: StateSubscriber): () => void {
    this.subscribers.add(fn);
    fn(this.state);
    return () => this.subscribers.delete(fn);
  }

  abort(): void {
    this.abortController?.abort();
    if (
      this.state.status !== 'idle' &&
      this.state.status !== 'success' &&
      this.state.status !== 'error' &&
      this.state.status !== 'skipped' &&
      this.state.status !== 'inventory_changed'
    ) {
      this.transition({
        status: 'error',
        reason: 'timeout',
        message: 'Auto-link was cancelled'
      });
    }
  }

  async startForEmptyInventory(context: AutoLinkContext): Promise<void> {
    if (this.state.status !== 'idle') {
      return;
    }

    this.abortController = new AbortController();
    const completionLifecycle: RunCompletionLifecycle = {
      accountId: context.userId,
      clearStarted: false,
      requestId: undefined,
      status: 'pending'
    };
    try {
      await this.runStateMachine(context, completionLifecycle);
    } finally {
      if (completionLifecycle.status === 'pending') {
        completionLifecycle.status = 'closed';
      }
    }
  }

  // Compatibility entry point for callers that use the general controller
  // name; the empty-inventory guard remains the authoritative start path.
  async start(context: AutoLinkContext): Promise<void> {
    await this.startForEmptyInventory(context);
  }

  private transition(newState: AutoLinkState): void {
    // Emit the whole-run total on the FIRST terminal transition. `ok` mirrors
    // whether the machine ended in success/skipped (boot proceeds) vs error.
    if (
      this.machineStartedAt !== undefined &&
      (newState.status === 'success' ||
        newState.status === 'error' ||
        newState.status === 'skipped')
    ) {
      console.log(
        `IRIS_DIAG_BOOT_HOP name=autoLink.total ms=${Date.now() - this.machineStartedAt} ok=${newState.status !== 'error'}`
      );
      this.machineStartedAt = undefined;
    }
    this.state = newState;
    this.options.onStateChange(newState);
    this.subscribers.forEach((fn) => fn(newState));
  }

  private async runStateMachine(
    ctx: AutoLinkContext,
    completionLifecycle: RunCompletionLifecycle
  ): Promise<void> {
    this.machineStartedAt = Date.now();
    const signal = this.abortController!.signal;
    const runContext: RunContext = {
      roomId: undefined,
      roomCreated: false,
      linkSucceeded: false
    };
    let phase: AutoLinkPhase = 'checking';
    let agentId: Agent['id'] | undefined;
    let requestId: AutoLinkRequestId | undefined;
    let bridgeId: Agent['id'] | undefined;

    // Check eligibility
    const skipReason = this.checkEligibility(ctx);
    if (skipReason) {
      autoLinkAnalytics.trackAutoLinkDisabled({ reason: skipReason });
      autoLinkAnalytics.trackAutoLinkManualFallback({ reason: skipReason });
      this.transition({ status: 'skipped', reason: skipReason });
      return;
    }

    // CHECKING state - get local bridge ID and check server
    this.transition({ status: 'checking', startedAt: Date.now() });

    try {
      // Bound the FIRST checking await. getToken() resolves the auth0 access
      // token, which on Windows depends on the bridge -> auth-sidecar token
      // path; when that sidecar hangs ("timeout while waiting for token") the
      // promise neither resolves nor rejects. Unbounded, it wedges the whole
      // checking phase indefinitely, so the controller never advances to
      // error/skipped and the BootOverlay stays opaque past the boot budget.
      // The bridge-id and link-details lookups below were already bounded by
      // ipcTimeoutMs (getLocalBridgeIdWithTimeout / getLinkAgentIdsWithTimeout)
      // but this earlier await was not — so a wedged token path slipped past
      // the deadline. Racing getToken() against the same ipcTimeoutMs/abort
      // deadline makes a fast fetch-failed/ECONNREFUSED OR a slow hang reject
      // promptly, driving checking -> error -> skipped -> overlay ready.
      const token = await timeBootHop('getToken', this.getTokenWithTimeout(ctx, signal));
      this.throwIfAborted(signal);

      // Per rule: regression-tests-for-state-machine-primitives (ai.knowledge/global) —
      // one authoritative zero gate must precede every success or mutation exit.
      if (!(await this.revalidateEmptyInventory(token, signal))) {
        return;
      }

      // Hop name kept as 'checkAlreadyCompleted' for boot-diag continuity even
      // though the underlying check is now the richer completion-record inspection.
      const completionRecord = await timeBootHop(
        'checkAlreadyCompleted',
        this.inspectCompletionRecord(ctx.userId)
      );
      const alreadyCompleted = completionRecord === 'valid';

      // Optimization: fire both IPC lookups in parallel using Promise.allSettled.
      // Previously these ran sequentially (each with its own timeout), adding
      // up to 2x ipcTimeoutMs of wall-clock time. Running in parallel cuts this
      // to max(ipcTimeout) in the worst case. If bridgeId resolves, we use it
      // for the fast "already linked" path; otherwise we fall through to linkDetails.
      // Both lookups still start in parallel — but checking must NOT block
      // on the Mailroom-backed link-details fetch before consulting LOCAL
      // truth. Pre-fix this was Promise.allSettled([...]) and a hung
      // getLinkAgentIds (300s ipcTimeoutMs) wedged the machine in `checking`
      // past every e2e boot budget while the sidecar already knew the agent
      // was linked (win runs 27326245074 / 27327489117: probes show
      // autolink-checking, opacity:1 for the full window). The local
      // bridge-id pull resolves in seconds; link details are awaited in
      // full only on the genuine link path below.
      const rawLinkDetailsPromise = getLinkAgentIds(true);
      // Existing-link paths never wait on Mailroom, but retain an already-ready
      // or late request id so verified remount recovery can retire its key.
      // Observe the raw request: resetting a successful controller aborts only
      // the bounded state-machine await, not this run-owned completion lifecycle.
      rawLinkDetailsPromise.then(
        (details) => {
          requestId = details.requestId;
          this.recordResolvedRequestId(completionLifecycle, details.requestId);
        },
        () => undefined
      );
      // timeBootHop measures from HERE (promise creation), so the hop reads
      // the lookup's true duration even though it is awaited further down.
      const linkDetailsPromise = timeBootHop(
        'getLinkAgentIds',
        this.getLinkAgentIdsWithTimeout(signal, rawLinkDetailsPromise)
      );
      // Fast success/skip paths may never consume the bounded await; absorb its
      // expected timeout/abort while the raw observer owns any late request id.
      void linkDetailsPromise.catch(() => undefined);
      const existingBridgeId = await timeBootHop(
        'getLocalBridgeId',
        this.getLocalBridgeIdWithTimeout(signal)
      ).catch(() => undefined);
      this.throwIfAborted(signal);

      // If we have an existing bridge ID, check if already linked
      if (existingBridgeId) {
        const serverCheck = await timeBootHop(
          'checkBridgeLinkedRoom',
          this.checkBridgeLinkedRoomBounded(existingBridgeId, token)
        );
        this.throwIfAborted(signal);

        if (serverCheck.alreadyLinked && serverCheck.roomId) {
          // ENG-4166 D2: "already linked to room X" only implies the AGENT link
          // exists — NOT that the current token can open room X. A cross-account
          // link (retry-2 signature: the backend registry retains the link;
          // wiping the local link DB cannot clear it) reads the agent yet 403s
          // the room. Verify room access BEFORE declaring success; a definitive
          // forbidden is a cross-account terminal, never a false success that
          // dooms boot. unknown (404 / registry weather) keeps today's adopt so
          // a slow registry never strands a healthy already-linked boot.
          // Per rule: reference-rules-in-comments (ai.knowledge/global).
          const access = await timeBootHop(
            'verifyRoomAccess',
            this.verifyRoomAccessBounded(serverCheck.roomId, token)
          );
          this.throwIfAborted(signal);
          if (access === 'forbidden') {
            console.warn(
              '[auto-link] Bridge already linked to a room this account cannot access — cross-account link, not success:',
              serverCheck.roomId
            );
            this.transitionCrossAccountLink(existingBridgeId);
            return;
          }
          await this.completeVerifiedLink(
            {
              bridgeId: existingBridgeId,
              roomId: serverCheck.roomId,
              successReason: 'already_linked',
              trackSuccess: !alreadyCompleted
            },
            completionLifecycle
          );
          return;
        }

        // Local truth outranks the registry for the already-linked steady
        // state: existingBridgeId only exists because the sidecar reported
        // LINKED — and the sidecar VALIDATES the auth triple against the
        // cloud for the CURRENT credentials at boot, wiping stale
        // cross-account links (authentication.go shouldShortCircuitE2ELink /
        // CheckAuthenticationData; commit 8bac44682). An account switch
        // therefore surfaces here as sidecar-UNLINKED, never as
        // linked-to-the-old-agent — the identity layer owns switch
        // correctness, so no renderer-side switch detection is needed (and
        // none is possible: link requests mint a fresh uuid per request, so
        // comparing ids is meaningless).
        //
        // BUT the sidecar's first answer can RACE that very validation: it
        // served a stale pre-wipe link 65ms before wiping it (CI run
        // 27332628785, win delay-amplification CV scenario — the machine
        // skipped on the stale answer and boot wedged in `skipped`, with the
        // bridge:reboot recovery suppressed under IRIS_E2E_TEST). So the two
        // registry outcomes diverge:
        //  - DEGRADED read (timeout / no answer): skip immediately. The
        //    registry didn't answer; a re-link on broken network cannot
        //    converge (linkAgentV2 participant-waits on a room the agent
        //    never joins — CloudWatch-verified 29s Lambda timeouts; runs
        //    27322897969 / 27327489117). Local truth wins.
        //  - DEFINITIVE not-linked: the cloud positively says no link. Trust
        //    the sidecar only if it re-confirms the link on a LIVE consult
        //    after a short grace (replica lag is real; a wiped triple is
        //    too). Unconfirmed → fall through to the genuine link path,
        //    which provably converges post-wipe (mac CV scenario, same run:
        //    re-link recovered in 18.4s).
        this.throwIfAborted(signal);
        // On a degraded read the first answer stands; on a definitive miss
        // only a live re-consult can confirm — and it may surface a NEWER id
        // than the first answer (a fresh sidecar link mints a new uuid), so
        // the live id is the one to prime with.
        const confirmedLocalId = serverCheck.degraded
          ? existingBridgeId
          : await timeBootHop('reconfirmSidecarLinkLive', this.reconfirmSidecarLinkLive(signal));
        this.throwIfAborted(signal);
        if (confirmedLocalId) {
          // ENG-4154 warm account-switch: "cloud-validated" above is only as
          // fresh as the sidecar's last PROCESS BOOT — a same-window
          // logout → login never re-runs that validation, so the sidecar
          // live-re-confirms the PREVIOUS account's link as truth and the
          // skip below strands the new account unlinked forever (local repro
          // 2026-06-11: B's registry definitively disowned agent 5b2a9b0b…,
          // the live re-consult re-affirmed it, the machine skipped
          // `already_linked_local`, and the UI settled on the install-bridge
          // screen). The discriminator is already IN the registry answer:
          // the agents/get Lambda runs its policy check before the existence
          // lookup, so `forbidden` (in-band 403) means the agent EXISTS
          // under an org this account cannot access — definitive
          // cross-account evidence — while a 404 stays ambiguous (fresh-link
          // replica lag reads as not-found). Only when (a) the answer was
          // definitive AND forbidden, and (b) the live re-consult
          // re-affirmed the SAME id the registry rejected (a different id
          // means a fresh link landed mid-check — not the stale pattern),
          // treat the sidecar's claim as a stale cross-account link: unlink
          // it (the sidecar wipes its credentials and reboots the app into
          // the fresh-install link path) instead of skipping. Anything
          // ambiguous keeps the skip — re-linking on uncertainty is the
          // non-convergent failure mode (linkAgentV2 participant-wait wedge,
          // runs 27322897969/27326245074). NOTE: an ownership probe via
          // listAgents was tried first and is NOT viable — the registry has
          // no agents/list Lambda (the subject 403s at the gateway, which
          // the renderer sees as an opaque "Failed to fetch").
          const staleCrossAccount =
            !serverCheck.degraded &&
            serverCheck.forbidden === true &&
            confirmedLocalId.toLowerCase() === existingBridgeId.toLowerCase();
          this.throwIfAborted(signal);
          if (staleCrossAccount) {
            // TRANSIENT-vs-CROSS-ACCOUNT discriminator (per-run-org refresh).
            // A single agents/get 403 is byte-identical in both cases
            // (forbidden:true, sidecar re-confirms the same id), so TIME is the
            // only discriminator. On a renderer page.reload the full link flow
            // re-runs; for an agent linked into THIS run's fresh per-run org
            // milliseconds earlier, the first checkBridgeLinkedRoom can read a
            // TRANSIENT 403 (registry replica still converging) that flips to a
            // readable 200 within seconds. A GENUINE cross-account 403 is a
            // stable policy fact that stays forbidden forever. Re-sample
            // agents/get ONCE more after a short bounded grace, GATED to exactly
            // this suspected-stale path (already narrow: !degraded && forbidden
            // && live-reconfirmed-same-id), so it runs rarely. The re-probe
            // returns fast (a 403/200 HTTP read, not a 15s timeout), so the
            // total cost is checking + ~2s reconfirm + ~2s grace + a fast
            // re-probe (~4-6s) — far inside the 30s refresh budget. Without this
            // the fresh-link 403 is misclassified cross-account → unlink, and
            // bridge:reboot is suppressed under IRIS_E2E_TEST (ipc.ts ~826) so
            // the loader dead-ends → B2 "Unable to connect" → 30s timeout.
            // Per rule: ai-code-requires-tests + code-comments-required +
            //   reference-rules-in-comments + think-in-systems (single bounded
            //   re-probe, reuses delayRespectingAbort) (ai.knowledge/global).
            await this.delayRespectingAbort(this.options.staleRevalidateGraceMs, signal);
            this.throwIfAborted(signal);
            const reprobe = await timeBootHop(
              'checkBridgeLinkedRoom.reprobe',
              this.checkBridgeLinkedRoomBounded(existingBridgeId, token)
            );
            this.throwIfAborted(signal);
            if (reprobe.alreadyLinked && reprobe.roomId) {
              // (a) The fresh link converged: agents/get now reads the link.
              // This was eventual-consistency lag, not cross-account — adopt
              // the link instead of unlinking. Mirrors the alreadyLinked
              // success path above (prime cache, persist, transition success).
              await this.completeVerifiedLink(
                {
                  bridgeId: existingBridgeId,
                  roomId: reprobe.roomId,
                  successReason: undefined,
                  trackSuccess: false
                },
                completionLifecycle
              );
              return;
            }
            if (reprobe.forbidden === true) {
              // (b) Still forbidden across the bounded window: a stable policy
              // fact = genuine cross-account link. Clear it exactly as before
              // (ENG-4154). The blanket-ignore failure mode is explicitly
              // avoided — a 403 that PERSISTS is still unlinked.
              await this.clearStaleCrossAccountLink(confirmedLocalId, signal);
              return;
            }
            // (c) Ambiguous (degraded read, or no-longer-forbidden but not yet
            // readable): never unlink on uncertainty. Fall through to the
            // existing skip(already_linked_local) — local truth wins.
          }
          console.warn(
            '[auto-link] Sidecar reports linked agent',
            confirmedLocalId,
            `but registry room lookup ${serverCheck.degraded ? 'was degraded' : 'found no link'} — skipping re-link (cloud-validated local truth wins).`
          );
          primeMachineAgentCache(confirmedLocalId);
          this.transition({ status: 'skipped', reason: 'already_linked_local' });
          return;
        }
        console.warn(
          '[auto-link] Sidecar-reported link for agent',
          existingBridgeId,
          'did not survive a live re-consult after a definitive registry miss — proceeding to the genuine link path.'
        );
        // Fall through to the genuine link path below.
      }

      // Get link request details from the parallel result (already resolved above).
      this.throwIfAborted(signal);
      // Genuine link path (sidecar unlinked, or account switch): NOW await
      // the link details in full — same bound (ipcTimeoutMs) and same error
      // semantics as the pre-restructure allSettled branch.
      let linkDetails: LinkAgentIdsResult;
      try {
        linkDetails = await linkDetailsPromise;
      } catch (linkDetailsError) {
        if (this.isAbortError(linkDetailsError) || signal.aborted) {
          throw linkDetailsError;
        }

        this.transition({
          status: 'error',
          reason: 'ipc_failed',
          message:
            linkDetailsError instanceof Error
              ? linkDetailsError.message
              : 'Could not get bridge link details'
        });
        return;
      }
      ({ agentId, requestId } = linkDetails);
      this.recordResolvedRequestId(completionLifecycle, requestId);
      this.throwIfAborted(signal);
      if (!agentId || !requestId) {
        autoLinkAnalytics.trackAutoLinkNoDevices({
          reason: 'missing_link_details',
          ...withOptionalRequestId(requestId)
        });
        this.transition({
          status: 'error',
          reason: 'ipc_failed',
          message: 'Could not get bridge link details'
        });
        return;
      }

      // If IPC bridge lookup timed out, verify the candidate bridge before creating anything.
      if (!existingBridgeId || existingBridgeId !== agentId) {
        const candidateCheck = await timeBootHop(
          'checkBridgeLinkedRoom.candidate',
          this.checkBridgeLinkedRoomBounded(agentId, token)
        );
        this.throwIfAborted(signal);

        if (candidateCheck.alreadyLinked && candidateCheck.roomId) {
          // ENG-4166 D2: same access gate as the existing-bridge path above —
          // an already-linked candidate room must be reachable under the current
          // token before we call it success. Forbidden → cross-account terminal.
          const access = await timeBootHop(
            'verifyRoomAccess.candidate',
            this.verifyRoomAccessBounded(candidateCheck.roomId, token)
          );
          this.throwIfAborted(signal);
          if (access === 'forbidden') {
            console.warn(
              '[auto-link] Candidate bridge already linked to a room this account cannot access — cross-account, not success:',
              candidateCheck.roomId
            );
            this.transitionCrossAccountLink(agentId);
            return;
          }
          await this.completeVerifiedLink(
            {
              bridgeId: agentId,
              roomId: candidateCheck.roomId,
              successReason: 'candidate_already_linked',
              trackSuccess: !alreadyCompleted
            },
            completionLifecycle
          );
          return;
        }

        // Same degraded-read fallback as the existing-bridge check above.
        if (candidateCheck.degraded && (await this.skipIfLocallyLinkedOnDegradedCheck(signal))) {
          return;
        }
      }
      // Always use the agentId from getLinkAgentIds - this is the ID that will be linked
      bridgeId = agentId;

      if (alreadyCompleted) {
        // The completion record is bridge-independent proof a link existed for
        // this user. Only a DEFINITIVE live sidecar "unlinked" (clean empty
        // reply — ipc.ts resolver contract) may overrule it. Silence/error
        // means an unhealthy bridge, not a fresh install — never createRoom.
        const live = await withTimeout(
          consultSidecarLinkLive(),
          this.options.serverCheckTimeoutMs,
          'bridgeHealthGate'
        ).catch(() => ({ state: 'unknown' as const }));
        this.throwIfAborted(signal);

        if (live.state === 'linked') {
          // Late-recovering sidecar still knows the link — same skip as the
          // degraded-read already_linked_local path.
          primeMachineAgentCache(live.agentId);
          this.transition({ status: 'skipped', reason: 'already_linked_local' });
          return;
        }
        if (live.state !== 'unlinked') {
          // Reuse the canonical bridge_link_failed row (reason is a free
          // string there) so PostHog can count how often the gate fires and
          // how many duplicate studios it prevented. No new event name, so
          // the analytics root and the posthog-selection receipt stay
          // untouched. Per rule: ai-code-requires-tests (asserted in the
          // gate suite).
          autoLinkAnalytics.trackAutoLinkError({
            ...withOptionalRequestId(requestId),
            reason: 'bridge_unhealthy',
            cause: 'bridge_health_gate'
          });
          this.transition({
            status: 'error',
            reason: 'bridge_unhealthy',
            message:
              'Bridge is not responding, but this computer is already linked. Restart the app to reconnect.'
          });
          return;
        }
        // Definitive unlinked: genuinely stale record (post-unlink re-link).
        // The stale record is cleared once the atomic create returns a room
        // owned by this request (LINKING phase below), never before.
      }

      // This quota check is read-only. Stale-attempt pruning is persisted only
      // by recordAttemptBestEffort after the final zero-inventory fence.
      const canAttempt = await this.canAttemptAutoLink(ctx.userId);
      if (!canAttempt) {
        autoLinkAnalytics.trackAutoLinkDisabled({
          ...withOptionalRequestId(requestId),
          reason: 'attempts_exhausted'
        });
        autoLinkAnalytics.trackAutoLinkManualFallback({
          ...withOptionalRequestId(requestId),
          reason: 'attempts_exhausted'
        });
        this.transition({ status: 'skipped', reason: 'attempts_exhausted' });
        return;
      }

      const studioName = await timeBootHop(
        'resolveStudioName',
        resolveAutoLinkStudioName({
          requestId,
          token,
          hostname: ctx.hostname,
          fetchLinkRequest: fetchRequestLink
        })
      );
      this.throwIfAborted(signal);
      // The initial zero snapshot precedes bridge/name discovery, which can
      // take seconds. Re-read immediately before the first run-owned write so
      // another machine's newly created Studio wins this race without a
      // duplicate attempt record, room, or link.
      if (!(await this.revalidateEmptyInventory(token, signal))) {
        return;
      }

      const idempotencyKey = await resolveAutoLinkCreateIdempotencyKey(ctx.userId, requestId);
      const roomResult: RoomCreationResult = await timeBootHop(
        'createRoom',
        createAutoLinkRoom(token, studioName, {
          idempotencyKey
        })
      );
      if (roomResult.status === 'inventory_changed') {
        // Another machine won after our last replica-side zero read. The
        // primary-database result is authoritative: never manufacture/link an
        // empty room ID, and do not burn an auto-link attempt for the loser.
        this.transition({
          status: 'inventory_changed',
          roomCount: roomResult.roomCount,
          rooms: [...roomResult.rooms],
          ownedOrganizations: [...roomResult.ownedOrganizations]
        });
        return;
      }

      // LINKING state — the atomic endpoint returned a room owned by this exact
      // logical request. Only now may local attempt/completion state mutate.
      phase = 'linking';
      if (completionRecord !== 'absent') {
        console.warn(
          `[auto-link] ${alreadyCompleted ? 'Completion record exists but bridge is not linked' : 'Completion record is stale or invalid'}. Clearing before the new attempt.`
        );
        await this.clearCompletionRecordBestEffort();
      }

      await this.recordAttemptBestEffort(ctx.userId);
      autoLinkAnalytics.trackAutoLinkAttempt({
        request_id: requestId,
        ...withOptionalAgentId(bridgeId)
      });

      runContext.roomId = roomResult.roomId;
      runContext.roomCreated = roomResult.created;
      runContext.authoritativeRoom = roomResult.authoritativeRoom;
      runContext.authoritativeOwnedOrganizations = roomResult.authoritativeOwnedOrganizations;
      this.throwIfAborted(signal);

      this.transition({
        status: 'linking',
        bridgeId: bridgeId!,
        roomId: roomResult.roomId
      });

      let linkedRoomId = roomResult.roomId;
      this.throwIfAborted(signal);
      // Retry transient linkAgent failures. On a fresh launcher boot the first
      // linkAgent NATS request can take 30s+ and surface as "Failed to fetch"
      // before the cloud connection fully establishes. The launcher's auth
      // recovery path then reboots the entire Electron app to retry — which
      // tears down any in-flight Playwright Page mid-assertion (see e2e tests).
      // Retrying the call here lets the NATS connection settle so the link
      // succeeds within the original boot, avoiding the destructive reboot.
      // 3 attempts × up to ~6s backoff = bounded by the auto-link-guard's
      // MAX_SETUP_TIME_MS budget.
      // Migration note: integrates analytics tracking from studio.client#1018
      // (auto-link conflict/recovery instrumentation) with the existing
      // platform-side retry loop.
      let linkSucceeded = false;
      let lastLinkError: unknown;
      // Canonical-event single-fire: the conflict/sidecar-race recovery paths
      // below BREAK out of the retry loop and fall through to the shared
      // verify+SUCCESS tail — emitting the success event at the recovery
      // site AND at the tail double-counts one logical link (win e2e
      // auto-link-studio-list receipt, run 30476163615: two sends 14ms apart,
      // same request_id). Recovery paths record their reason here instead; the
      // single emit lives at the SUCCESS tail, which also means a recovery that
      // later fails verification never reports success at all.
      let recoveredSuccessReason: 'conflict_recovered' | undefined;
      // Pre-warm mailroom Lambdas. Each RPC subject maps to a distinct Lambda,
      // so we warm both listAgents and linkAgent. The sentinel probe uses
      // zero-byte IDs (guaranteed non-matching) — most errors prove the
      // Lambda responded; atob DOMException is logged as a corruption warning
      // (real failure, see auto-link-prewarm.ts notes). Result is informational;
      // retry loop proceeds regardless.
      //
      // Optimization: fire prewarm as fire-and-forget so the first linkAgent
      // attempt races with it. If attempt #1 fails, await prewarm completion
      // before retry #2 to guarantee Lambdas are warm. This eliminates the
      // serial 2.5s prewarm blocking before the first link attempt.
      const SENTINEL_HEX = toHexFromId(new Uint8Array(16))!;
      const linkAgentSentinelProbe = async (probeToken: string): Promise<void> => {
        try {
          // Expected to fail — zero IDs won't match. Lambda warms regardless.
          await linkAgent(
            SENTINEL_HEX as Agent['id'],
            SENTINEL_HEX as LinkRequest['id'],
            SENTINEL_HEX as Room['id'],
            probeToken
          );
        } catch (sentinelError) {
          // Any error = Lambda responded = warm. Swallow intentionally.
          void sentinelError;
        }
      };
      // Fire-and-forget: start prewarm in background, don't await yet.
      const prewarmPromise = prewarmMailroomHttpEndpoint(token, signal, [
        listAgents,
        linkAgentSentinelProbe
      ]);
      // Ensure prewarm errors don't become unhandled rejections.
      prewarmPromise.catch((prewarmErr) => {
        console.debug('[auto-link] Prewarm background error (non-fatal):', prewarmErr);
      });
      let prewarmAwaited = false;
      // Sidecar race, resolved LIVE: the identity-bootstrap sidecar sends its
      // own link request at boot, and when it wins, the agent joins the
      // SIDECAR's room — this loop's linkAgent then burns the Lambda's full
      // 29s participant-wait per attempt while the boot overlay blocks every
      // click (mac run 27330652135 account-settings; e2e-hil 'Connecting
      // overlay kept intercepting pointer events'). Post-failure adoption
      // (the conflict recovery below) is ~30s too late for 30s click
      // budgets. So each attempt RACES linkAgent against the identity store
      // settling: the sidecar's link is itself a registry link, so the
      // moment identity reports linked, probeLinkConflictRecovery can adopt
      // the registry room immediately.
      const IDENTITY_WIN = Symbol('identity-linked');
      const identityLinkedWatcher = resolveLinkedAgentId(false)
        .then((id) => (id ? IDENTITY_WIN : new Promise<never>(() => {})))
        .catch(() => new Promise<never>(() => {}));
      for (let linkAttempt = 1; linkAttempt <= MAX_LINK_ATTEMPTS; linkAttempt++) {
        try {
          // ONE in-flight linkAgent per attempt — the identity win adopts
          // without firing a second request; on a failed adopt we await the
          // SAME attempt rather than double-firing.
          // Keep the F2 boot-progress clock fresh during this ~29s cloud
          // participant-wait so the watchdog does not reboot a healthy slow link
          // (S8 / GAP1). Heartbeat stops the instant linkAgent settles.
          const attemptPromise = timeBootHop(
            `linkAgent.attempt${linkAttempt}`,
            withBootProgressHeartbeat(
              linkAgent(agentId, requestId, roomResult.roomId, token),
              BOOT_PROGRESS_HEARTBEAT_MS
            )
          );
          // The adopt path may break out before consuming the attempt.
          attemptPromise.catch(() => {});
          const raced = await Promise.race([
            attemptPromise.then(() => undefined),
            identityLinkedWatcher
          ]);
          if (raced === IDENTITY_WIN) {
            this.throwIfAborted(signal);
            console.warn(
              '[auto-link] identity store reported linked mid-linkAgent (sidecar won the race) — adopting its registry room'
            );
            const raceRecovery = await withTimeout(
              this.probeLinkConflictRecovery({
                bridgeId: bridgeId!,
                token
              }),
              this.options.serverCheckTimeoutMs,
              'identityRaceAdopt'
            ).catch(() => ({ status: 'unknown' as const }));
            if (raceRecovery.status === 'recovered') {
              await this.applyRecoveredLink(runContext, ctx, raceRecovery.roomId);
              linkedRoomId = raceRecovery.roomId;
              // Success is emitted ONCE at the shared tail (see
              // recoveredSuccessReason above) — not here.
              recoveredSuccessReason = 'conflict_recovered';
              linkSucceeded = true;
              break;
            }
            if (raceRecovery.status === 'forbidden') {
              // ENG-4166 D2: the sidecar won the race into a room this account
              // cannot access — adopting it would be a false success into a
              // doomed boot. Clean up our created room and terminate; D1/D3
              // route to the boot-error screen.
              await this.cleanupCreatedRoom(runContext, ctx);
              this.transitionCrossAccountLink(bridgeId);
              return;
            }
            // Registry hasn't surfaced the sidecar's room yet — let this
            // attempt's already-in-flight linkAgent settle on its own.
            await attemptPromise;
          }
          runContext.linkSucceeded = true;
          linkSucceeded = true;
          break;
        } catch (linkError) {
          lastLinkError = linkError;
          // Log atob DOMException distinctly. Per iter-12 W14 root cause: this
          // is a REAL response-body corruption (non-base64), not a transient
          // warm signal — see auto-link-prewarm.ts and the
          // computeLinkRetryBackoffMs comment for the full mechanism.
          if (isAtobDomException(linkError)) {
            console.warn(
              `[auto-link] Mailroom HTTP returned non-base64 body (atob DOMException) — real corruption. linkAgent attempt ${linkAttempt}/${MAX_LINK_ATTEMPTS}`
            );
          }
          const conflictKind = this.getLinkConflictKind(linkError);
          if (conflictKind) {
            autoLinkAnalytics.trackAutoLinkRetry({
              ...withOptionalRequestId(requestId),
              reason: 'link_conflict',
              ...withOptionalAgentId(bridgeId)
            });

            const conflictRecovery = await this.probeLinkConflictRecovery({
              bridgeId: bridgeId!,
              token
            });

            if (conflictRecovery.status === 'recovered') {
              await this.applyRecoveredLink(runContext, ctx, conflictRecovery.roomId);
              linkedRoomId = conflictRecovery.roomId;
              // Success is emitted ONCE at the shared tail (see
              // recoveredSuccessReason above) — not here.
              recoveredSuccessReason = 'conflict_recovered';
              linkSucceeded = true;
              break;
            } else if (conflictRecovery.status === 'forbidden') {
              // ENG-4166 D2: the conflicting link is to a room this account
              // cannot access (cross-account) — do not adopt it as success.
              // Clean up our created room and terminate; D1/D3 route to the
              // boot-error screen.
              await this.cleanupCreatedRoom(runContext, ctx);
              this.transitionCrossAccountLink(bridgeId);
              return;
            } else if (conflictRecovery.status === 'unknown') {
              // Preserve room for confirmed API conflicts where link may have
              // succeeded despite a failed recovery check.
              if (conflictKind === 'api_conflict') {
                runContext.linkSucceeded = true;
              }
              throw linkError;
            } else {
              throw linkError;
            }
          }

          // ENG-4154 sidecar-race recovery: the identity-bootstrap sidecar
          // sends its OWN link request at boot. When it wins the race (slow
          // boots: win CI runs 27312266829/27319748517), the agent joins the
          // SIDECAR's room — this machine's linkAgent can then never succeed:
          // linkAgentV2's participant-wait spins on a room the agent will
          // never join, times out at the Lambda's 29s cap, and the gateway
          // returns a non-proto body (the atob DOMException). The registry is
          // the source of truth: if the agent is already linked, adopt that
          // room exactly like the conflict path above. Bounded so a hung
          // registry fetch degrades to a normal retry (ENG-4034 bug class).
          const sidecarRaceRecovery = await withTimeout(
            this.probeLinkConflictRecovery({
              bridgeId: bridgeId!,
              token
            }),
            this.options.serverCheckTimeoutMs,
            'sidecarRaceRecovery'
          ).catch(() => ({ status: 'unknown' as const }));
          if (sidecarRaceRecovery.status === 'recovered') {
            await this.applyRecoveredLink(runContext, ctx, sidecarRaceRecovery.roomId);
            console.warn(
              `[auto-link] linkAgent attempt ${linkAttempt} failed but the agent is already linked (sidecar link won the race) — adopting room ${sidecarRaceRecovery.roomId}`
            );
            linkedRoomId = sidecarRaceRecovery.roomId;
            // Success is emitted ONCE at the shared tail (see
            // recoveredSuccessReason above) — not here.
            recoveredSuccessReason = 'conflict_recovered';
            linkSucceeded = true;
            break;
          }
          if (sidecarRaceRecovery.status === 'forbidden') {
            // ENG-4166 D2: the sidecar's room is cross-account — do not adopt it
            // as success. Clean up our created room and terminate; D1/D3 route
            // to the boot-error screen.
            await this.cleanupCreatedRoom(runContext, ctx);
            this.transitionCrossAccountLink(bridgeId);
            return;
          }
          // 'not_linked' / 'unknown' → genuinely not linked yet (or registry
          // weather): fall through to the normal bounded backoff and retry.

          // Bounded linear backoff for ALL non-conflict errors (including
          // atob DOMException). Prior version short-circuited atob to 0ms
          // because of a flawed "Lambda already warm" theory; that triggered
          // 3 instant retries inside <1s and consistently failed Mac cold-
          // start auto-link. See computeLinkRetryBackoffMs for full
          // mechanism analysis.
          if (linkAttempt >= MAX_LINK_ATTEMPTS) {
            throw linkError;
          }
          const backoffMs = computeLinkRetryBackoffMs(
            linkError,
            linkAttempt,
            LINK_RETRY_BACKOFF_BASE_MS
          );
          console.warn(
            `[auto-link] linkAgent attempt ${linkAttempt}/${MAX_LINK_ATTEMPTS} failed; retrying in ${backoffMs}ms:`,
            linkError instanceof Error ? linkError.message : linkError
          );
          // Before retry #2, ensure prewarm has completed so Lambdas are warm.
          // This overlaps with the backoff sleep — whichever finishes last wins.
          if (!prewarmAwaited) {
            prewarmAwaited = true;
            await prewarmPromise.catch(() => {
              // Non-fatal: prewarm failure doesn't block retry.
            });
          }
          // eslint-disable-next-line promise/avoid-new, no-promise-executor-return
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          this.throwIfAborted(signal);
        }
      }
      if (!linkSucceeded && lastLinkError) {
        throw lastLinkError;
      }
      this.throwIfAborted(signal);

      // VERIFYING_LINK state - wait for link association to propagate
      phase = 'verifying_link';
      this.transition({
        status: 'verifying_link',
        bridgeId: bridgeId!,
        roomId: linkedRoomId,
        linkedAt: Date.now()
      });

      // S8 / GAP1: waitForBridgeLinked polls the cloud for up to
      // bridgeOnlineTimeoutMs with no auto-link state transition; keep the F2
      // progress clock fresh while it runs so a healthy-but-slow verify is not
      // rebooted. Heartbeat stops when the poll loop settles.
      const bridgeResult = await timeBootHop(
        'waitForBridgeLinked',
        withBootProgressHeartbeat(
          waitForBridgeLinked(
            bridgeId!,
            linkedRoomId,
            token,
            signal,
            this.options.bridgeOnlineTimeoutMs
          ),
          BOOT_PROGRESS_HEARTBEAT_MS
        )
      );
      this.throwIfAborted(signal);

      if (!bridgeResult.linked) {
        await this.cleanupCreatedRoom(runContext, ctx);
        if (bridgeResult.timedOut) {
          autoLinkAnalytics.trackAutoLinkTimeout({
            room_id: linkedRoomId,
            ...withOptionalRequestId(requestId),
            reason: 'verification_timed_out'
          });
        } else {
          autoLinkAnalytics.trackAutoLinkFailure({
            room_id: linkedRoomId,
            ...withOptionalRequestId(requestId),
            reason: 'verification_cancelled',
            result: 'failure'
          });
        }
        autoLinkAnalytics.trackAutoLinkManualFallback({
          room_id: linkedRoomId,
          ...withOptionalRequestId(requestId),
          reason: 'verification_failed'
        });
        this.transition({
          status: 'error',
          reason: 'timeout',
          message: bridgeResult.timedOut
            ? 'Bridge link verification timed out'
            : 'Bridge link verification was cancelled'
        });
        return;
      }

      // ENG-4166 D2: verifying_link declared success (retry-1 signature) even
      // while its own bridge-monitor polls were failing API-403 — because the
      // linked room can be a room ADOPTED cross-account (via applyRecoveredLink)
      // rather than the one we created under the current account. Verify room
      // access before the FINAL success so `success` always implies the room is
      // reachable. Freshly-created rooms are accessible (unknown/accessible →
      // proceed); a definitive forbidden is a cross-account terminal.
      // Per rule: reference-rules-in-comments (ai.knowledge/global).
      const finalAccess = await timeBootHop(
        'verifyRoomAccess.final',
        this.verifyRoomAccessBounded(linkedRoomId, token)
      );
      this.throwIfAborted(signal);
      if (finalAccess === 'forbidden') {
        await this.cleanupCreatedRoom(runContext, ctx);
        console.warn(
          '[auto-link] Verified link resolved to a room this account cannot access — cross-account, not success:',
          linkedRoomId
        );
        this.transitionCrossAccountLink(bridgeId);
        return;
      }

      await this.completeVerifiedLink(
        {
          bridgeId,
          roomId: linkedRoomId,
          // Carries the recovery provenance forward so PostHog keeps the
          // conflict_recovered signal the per-site emits used to provide.
          successReason: recoveredSuccessReason,
          trackSuccess: true,
          ...(runContext.authoritativeRoom !== undefined &&
          toHexFromId<Room['id']>(runContext.authoritativeRoom.id) === linkedRoomId
            ? {
                authoritativeRoom: runContext.authoritativeRoom,
                authoritativeOwnedOrganizations: runContext.authoritativeOwnedOrganizations
              }
            : {})
        },
        completionLifecycle
      );
    } catch (error) {
      console.error('[auto-link] Error:', error);

      const isAbort = this.isAbortError(error) || signal.aborted;

      // Only emit the generic error event for non-abort errors.
      // Abort errors are tracked specifically as timeouts below to
      // avoid double-counting the same failure in PostHog.
      const networkFailure = isNetworkError(error);
      // Product: checking-phase network-shaped errors still skip (same as
      // before this ticket). Telemetry: only a laptop the browser says is
      // offline is cleanup. A "Failed to fetch" while online still skips
      // the UI and still counts as result=failure.
      const isSelfHealNetworkSkip = networkFailure && phase === 'checking';
      const isHousekeepingNetworkSkip = isSelfHealNetworkSkip && isOffline(error);

      // ENG-4444: trackAutoLinkError and trackAutoLinkFailure both map to the
      // canonical bridge_link_failed name (BRIDGE_LINK_FAILED_EVENT_NAMES
      // merges LINK_ERROR and LINK_FAILURE onto it). The checking phase
      // (below, non-network) and the linking phase ALSO call
      // trackAutoLinkFailure for this exact caught error, so firing
      // trackAutoLinkError here too double-emits one canonical row per
      // occurrence. Suppress it only where that phase-specific leg is about
      // to fire; the network-unavailable-during-checking skip path and the
      // verifying_link/unknown-phase paths are unchanged (out of scope here).
      const hasPhaseSpecificFailureEvent =
        (phase === 'checking' && !networkFailure) || phase === 'linking';

      if (!isAbort && !hasPhaseSpecificFailureEvent) {
        autoLinkAnalytics.trackAutoLinkError({
          ...withOptionalRequestId(requestId),
          ...withOptionalAgentId(bridgeId),
          error_message: error instanceof Error ? error.message : String(error),
          reason: networkFailure ? 'network_unavailable' : phase,
          // Housekeeping, not failure: keeps the row (and `reason`) counted
          // for anyone who wants the raw total, while opting out of the
          // default 'failure' result so the corrected failure rate excludes it.
          // Written as an explicit ternary rather than a conditional spread with
          // `as const` — the global "never use type assertions" instruction, and
          // the literal union infers correctly in this contextually-typed slot.
          result: isHousekeepingNetworkSkip ? 'housekeeping' : 'failure',
          ...(isHousekeepingNetworkSkip ? { severity: EVENT_SEVERITY.S3_WARN } : {})
        });
      }

      if (isAbort) {
        await this.cleanupCreatedRoom(runContext, ctx);
        autoLinkAnalytics.trackAutoLinkTimeout({
          ...withOptionalRequestId(requestId),
          ...withOptionalAgentId(bridgeId),
          reason: 'aborted'
        });
        if (this.state.status !== 'success' && this.state.status !== 'error') {
          this.transition({
            status: 'error',
            reason: 'timeout',
            message: ABORT_ERROR_MESSAGE
          });
        }
        return;
      }

      // Product skip stays on isSelfHealNetworkSkip (text match). The
      // housekeeping stamp above is the narrower offline bit. They are
      // allowed to differ: this ticket is telemetry-only.
      if (isSelfHealNetworkSkip) {
        this.transition({ status: 'skipped', reason: 'network_unavailable' });
        return;
      }

      const message = error instanceof Error ? error.message : 'Unknown error';

      if (phase === 'checking') {
        autoLinkAnalytics.trackAutoLinkFailure({
          ...withOptionalRequestId(requestId),
          reason: 'server_check_failed',
          error_message: message,
          result: 'failure'
        });
        this.transition({
          status: 'error',
          reason: 'server_check_failed',
          message
        });
      } else if (phase === 'linking') {
        await this.cleanupCreatedRoom(runContext, ctx);
        autoLinkAnalytics.trackAutoLinkFailure({
          ...withOptionalRequestId(requestId),
          ...withOptionalAgentId(bridgeId),
          reason: 'bridge_link_failed',
          error_message: message,
          result: 'failure'
        });
        autoLinkAnalytics.trackAutoLinkManualFallback({
          ...withOptionalRequestId(requestId),
          reason: 'bridge_link_failed'
        });
        this.transition({
          status: 'error',
          reason: 'bridge_link_failed',
          message
        });
      } else if (phase === 'verifying_link') {
        await this.cleanupCreatedRoom(runContext, ctx);
        autoLinkAnalytics.trackAutoLinkTimeout({
          ...withOptionalRequestId(requestId),
          ...withOptionalAgentId(bridgeId),
          reason: 'verification_timeout',
          error_message: message
        });
        this.transition({
          status: 'error',
          reason: 'timeout',
          message
        });
      } else {
        this.transition({
          status: 'error',
          reason: 'timeout',
          message
        });
      }
    }
  }

  private throwIfAborted(signal: AbortSignal): void {
    if (!signal.aborted) {
      return;
    }

    const abortError = new Error(ABORT_ERROR_MESSAGE);
    abortError.name = ABORT_ERROR_NAME;
    throw abortError;
  }

  private isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === ABORT_ERROR_NAME;
  }

  private isApiConflictError(error: unknown): boolean {
    if (error instanceof ApiErrorClass && error.code === StatusCode.ClientErrorConflict) {
      return true;
    }

    return false;
  }

  private getLinkConflictKind(error: unknown): 'api_conflict' | 'message_conflict' | undefined {
    if (this.isApiConflictError(error)) {
      return 'api_conflict';
    }

    if (!(error instanceof Error)) {
      return undefined;
    }

    const message = error.message.toLowerCase();
    return LINK_CONFLICT_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern))
      ? 'message_conflict'
      : undefined;
  }

  // PURE probe — no side effects. Call sites wrap this in withTimeout
  // (Promise.race, not abortable): if side effects lived in here, a probe
  // resolving AFTER its timeout could still delete rooms / mutate runContext
  // long after the link loop moved on. Side effects live in
  // applyRecoveredLink, which the caller invokes only after ACCEPTING the
  // recovery result.
  private async probeLinkConflictRecovery({
    bridgeId,
    token
  }: {
    bridgeId: Agent['id'];
    token: string;
  }): Promise<
    | { readonly status: 'recovered'; readonly roomId: Room['id'] }
    | { readonly status: 'forbidden'; readonly roomId: Room['id'] }
    | { readonly status: 'not_linked' }
    | { readonly status: 'unknown' }
  > {
    try {
      const serverCheck = await checkBridgeLinkedRoom(bridgeId, token);

      if (!serverCheck.alreadyLinked || !serverCheck.roomId) {
        return { status: 'not_linked' };
      }

      // ENG-4166 D2: only ADOPT a recovered room the current token can open.
      // Every adopt site (identity-race, link-conflict, sidecar-race) funnels
      // through here, so gating adoption on room access closes all three at
      // once instead of forking a parallel check. A definitive forbidden is a
      // cross-account link → surfaced as `forbidden` so the caller terminates
      // rather than adopting an inaccessible room as success. unknown (404 /
      // weather) still adopts — never destroy a link on registry weather.
      // Per rule: reference-rules-in-comments (ai.knowledge/global).
      const access = await this.verifyRoomAccessBounded(serverCheck.roomId, token);
      if (access === 'forbidden') {
        console.warn(
          '[auto-link] Recovered link points at a room this account cannot access (cross-account):',
          serverCheck.roomId
        );
        return { status: 'forbidden', roomId: serverCheck.roomId };
      }

      console.warn(
        '[auto-link] Link request conflicted, but bridge is already linked to room:',
        serverCheck.roomId
      );

      return { status: 'recovered', roomId: serverCheck.roomId };
    } catch (error) {
      console.warn('[auto-link] Failed to recover from link conflict:', error);
      return { status: 'unknown' };
    }
  }

  // Apply side effects only after the caller accepts the recovery result;
  // timeout races may resolve late. cleanupRoom swallows its own errors, so
  // this never throws into the accept path.
  private async applyRecoveredLink(
    runContext: RunContext,
    context: AutoLinkContext,
    roomId: Room['id']
  ): Promise<void> {
    if (runContext.roomCreated && runContext.roomId && runContext.roomId !== roomId) {
      await this.cleanupRoom(runContext.roomId, context);
    }

    runContext.roomId = roomId;
    runContext.linkSucceeded = true;
  }

  private async cleanupCreatedRoom(runContext: RunContext, ctx: AutoLinkContext): Promise<void> {
    if (!runContext.roomId || !runContext.roomCreated || runContext.linkSucceeded) {
      return;
    }

    await this.cleanupRoom(runContext.roomId, ctx);
  }

  private async cleanupRoom(roomId: Room['id'], ctx: AutoLinkContext): Promise<void> {
    try {
      const token = await ctx.getToken();
      await deleteAutoLinkRoom(roomId, token);
    } catch (cleanupError) {
      console.error('[auto-link] Cleanup failed (non-fatal):', cleanupError);
    }
  }

  private checkEligibility(ctx: AutoLinkContext): 'not_electron' | 'not_iris_vendor' | undefined {
    // BYPASS PROTECTION (ENG-4371): re-derive eligibility from LIVE runtime
    // state at the enforcing layer, never from the caller-supplied ctx alone.
    // `isElectronApp()` is the live host check; `config.vendor` is the live
    // runtime vendor. A stale / garbage / contradictory `ctx.vendor` (e.g. a
    // direct `controller.start()` carrying vendor:'iris' while the runtime is
    // Hive) can no longer flip the contract — the single fail-closed vendor
    // decision wins, and the observable not_electron / not_iris_vendor skip
    // reason is preserved.
    // Per rule: reference-rules-in-comments (ai.knowledge/global) — fail-closed
    // on contradictory state; unknown vendor never auto-links.
    const decision = evaluateAutoLinkVendor({
      isElectron: ctx.isElectron && isElectronApp(),
      vendor: config.vendor
    });
    return decision.eligible ? undefined : decision.reason;
  }

  private async revalidateEmptyInventory(token: string, signal: AbortSignal): Promise<boolean> {
    try {
      const rooms = await withBootProgressHeartbeat(
        withTimeout(listRooms(token), this.options.serverCheckTimeoutMs, 'revalidateInventory'),
        BOOT_PROGRESS_HEARTBEAT_MS
      );
      for (const room of rooms) {
        if (toHexFromId<Room['id']>(room.id) === undefined) {
          throw new MalformedStudioInventoryError();
        }
      }
      this.throwIfAborted(signal);

      if (rooms.length > 0) {
        this.transition({ status: 'inventory_changed', roomCount: rooms.length });
        return false;
      }

      return true;
    } catch (listError) {
      if (this.isAbortError(listError) || signal.aborted) {
        throw listError;
      }
      this.transition({
        status: 'error',
        reason: 'server_check_failed',
        message:
          listError instanceof Error ? listError.message : 'Could not revalidate Studio inventory'
      });
      return false;
    }
  }

  private recordResolvedRequestId(
    lifecycle: RunCompletionLifecycle,
    requestId: AutoLinkRequestId | undefined
  ): void {
    if (requestId === undefined || lifecycle.status === 'closed') {
      return;
    }

    lifecycle.requestId = requestId;
    if (lifecycle.status === 'verified') {
      // Run-local lifecycle ownership fences this cleanup to its account epoch.
      void this.clearVerifiedCreateKey(lifecycle);
    }
  }

  private async clearVerifiedCreateKey(lifecycle: RunCompletionLifecycle): Promise<void> {
    if (
      lifecycle.status !== 'verified' ||
      lifecycle.requestId === undefined ||
      lifecycle.clearStarted
    ) {
      return;
    }

    lifecycle.clearStarted = true;
    await clearAutoLinkCreateIdempotencyKey(lifecycle.accountId, lifecycle.requestId);
  }

  private async completeVerifiedLink(
    options: VerifiedCompletionOptions,
    lifecycle: RunCompletionLifecycle
  ): Promise<void> {
    primeMachineAgentCache(options.bridgeId);
    await this.persistCompletionBestEffort(lifecycle.accountId, options.roomId);
    await this.clearAttemptsForUser(lifecycle.accountId);
    // Per rule: settle-every-terminal-branch (ai.knowledge/global) — this run
    // alone can retire its key, whether the request id arrived early or late.
    lifecycle.status = 'verified';
    await this.clearVerifiedCreateKey(lifecycle);
    if (options.trackSuccess) {
      const event = {
        room_id: options.roomId,
        ...withOptionalRequestId(lifecycle.requestId)
      };
      autoLinkAnalytics.trackAutoLinkSuccess(
        options.successReason === undefined ? event : { ...event, reason: options.successReason }
      );
    } else {
      // Say so. The dedup itself is correct — re-emitting success on every
      // boot of an already-linked machine would inflate the funnel — but the
      // suppression was SILENT, and a suppressed emit is indistinguishable
      // from a dropped one downstream: the ENG-4311 receipt reported
      // `bridge_link_attempt not observed within 90000ms` on a warm profile
      // and read as a rename regression when the code path had simply not
      // run. This line, and the e2e precondition gate that greps for it
      // (e2e/helpers/auto-link-preconditions.ts), are what separate
      // "intentionally not emitted" from "silently dropped".
      // Per rule: reference-rules-in-comments (ai.knowledge/global).
      console.warn(
        '[auto-link] success telemetry SUPPRESSED (completion record already present): ' +
          'bridge_link_succeeded / auto_link_bridge_success intentionally not emitted for room',
        options.roomId
      );
    }
    this.transition({
      status: 'success',
      roomId: options.roomId,
      ...(options.authoritativeRoom === undefined
        ? {}
        : {
            room: options.authoritativeRoom,
            ...(options.authoritativeOwnedOrganizations === undefined
              ? {}
              : { ownedOrganizations: [...options.authoritativeOwnedOrganizations] })
          })
    });
  }

  /** Inspect persisted completion without mutating before the final inventory fence. */
  private async inspectCompletionRecord(userId: string): Promise<CompletionRecordInspection> {
    try {
      const data = await persistenceLayer.get(PERSISTENCE_KEY_COMPLETED);
      if (!data) return 'absent';

      const record = JSON.parse(data) as CompletionRecord;

      if (record.userId !== userId) {
        return 'stale';
      }

      if (record.version !== AUTO_LINK_VERSION) {
        return 'stale';
      }

      return 'valid';
    } catch (error) {
      console.warn('[auto-link] Failed to parse completion record:', error);
      return 'stale';
    }
  }

  private async markCompleted(userId: string, roomId: Room['id']): Promise<void> {
    const record: CompletionRecord = {
      completedAt: Date.now(),
      roomId,
      userId,
      version: AUTO_LINK_VERSION
    };

    await persistenceLayer.set(PERSISTENCE_KEY_COMPLETED, JSON.stringify(record));
  }

  private async clearCompletionRecordBestEffort(): Promise<void> {
    try {
      await persistenceLayer.remove(PERSISTENCE_KEY_COMPLETED);
    } catch (error) {
      console.warn('[auto-link] Failed to clear completion record (non-fatal):', error);
    }
  }

  private async persistCompletionBestEffort(userId: string, roomId: Room['id']): Promise<boolean> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= PERSISTENCE_WRITE_RETRIES; attempt++) {
      try {
        await this.markCompleted(userId, roomId);
        return true;
      } catch (error) {
        lastError = error;

        if (attempt < PERSISTENCE_WRITE_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 100 * attempt));
        }
      }
    }

    console.warn('[auto-link] Failed to persist completion record after retries:', {
      roomId,
      userId,
      error: lastError
    });
    try {
      this.options.onNonFatalError('persistence_write_failed', lastError);
    } catch (callbackError) {
      console.error('[auto-link] onNonFatalError callback threw:', callbackError);
    }

    return false;
  }

  private async canAttemptAutoLink(userId: string): Promise<boolean> {
    if (!userId) {
      return true;
    }

    const now = Date.now();
    const record = await this.readAttemptRecord();
    const attempts = this.pruneAttempts(record.attempts, now);

    const recentAttempts = attempts.filter(
      (attempt) => attempt.userId === userId && now - attempt.timestamp < ATTEMPT_WINDOW_MS
    );

    return recentAttempts.length < MAX_ATTEMPTS_PER_WINDOW;
  }

  private async recordAttemptBestEffort(userId: string): Promise<void> {
    if (!userId) {
      return;
    }

    const now = Date.now();
    const record = await this.readAttemptRecord();
    const attempts = this.pruneAttempts(record.attempts, now);
    attempts.push({ timestamp: now, userId });
    await this.persistAttemptRecordBestEffort({ attempts });
  }

  // User-scoped cleanup keeps another account's throttle history intact.
  async clearAttemptsForUser(userId: string): Promise<void> {
    if (!userId) {
      return;
    }

    const now = Date.now();
    const record = await this.readAttemptRecord();
    const attempts = this.pruneAttempts(record.attempts, now).filter(
      (attempt) => attempt.userId !== userId
    );

    await this.persistAttemptRecordBestEffort({ attempts });
  }

  private async readAttemptRecord(): Promise<AttemptRecord> {
    try {
      const data = await persistenceLayer.get(PERSISTENCE_KEY_ATTEMPTS);

      if (!data) {
        return { attempts: [] };
      }

      const parsed = JSON.parse(data) as Partial<AttemptRecord>;

      if (!Array.isArray(parsed.attempts)) {
        return { attempts: [] };
      }

      return { attempts: parsed.attempts as AttemptEntry[] };
    } catch (error) {
      console.warn('[auto-link] Failed to read attempt record:', error);
      return { attempts: [] };
    }
  }

  private pruneAttempts(attempts: readonly AttemptEntry[], now: number): AttemptEntry[] {
    return attempts.filter((attempt): attempt is AttemptEntry => {
      if (!attempt || typeof attempt.timestamp !== 'number' || typeof attempt.userId !== 'string') {
        return false;
      }

      return now - attempt.timestamp < ATTEMPT_RETENTION_MS;
    });
  }

  private async persistAttemptRecordBestEffort(record: AttemptRecord): Promise<void> {
    try {
      if (record.attempts.length === 0) {
        await persistenceLayer.remove(PERSISTENCE_KEY_ATTEMPTS);
        return;
      }

      await persistenceLayer.set(PERSISTENCE_KEY_ATTEMPTS, JSON.stringify(record));
    } catch (error) {
      console.warn('[auto-link] Failed to persist attempt record (non-fatal):', error);
    }
  }

  // Bound ctx.getToken() against ipcTimeoutMs + the run's AbortSignal. Unlike
  // the bridge-id lookup (which resolves undefined on timeout so the candidate
  // path can still run), a token timeout is fatal to the checking phase: every
  // downstream server check needs the token. We therefore REJECT on timeout so
  // the runStateMachine catch block transitions checking -> error, which the
  // guard then holds for ERROR_DELAY_MS and flips to skipped — dismissing the
  // overlay. A fetch-failed/ECONNREFUSED reject from getToken() itself
  // propagates immediately (no need to wait for the deadline).
  private async getTokenWithTimeout(ctx: AutoLinkContext, signal: AbortSignal): Promise<string> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (callback: () => void): void => {
        if (settled) {
          return;
        }
        settled = true;
        signal.removeEventListener('abort', onAbort);
        clearTimeout(timeoutId);
        callback();
      };

      const timeoutId = setTimeout(() => {
        settle(() => reject(new Error('Timed out while getting auth token')));
      }, this.options.ipcTimeoutMs);

      const onAbort = (): void => {
        const abortError = new Error(ABORT_ERROR_MESSAGE);
        abortError.name = ABORT_ERROR_NAME;
        settle(() => reject(abortError));
      };

      signal.addEventListener('abort', onAbort);

      ctx
        .getToken()
        .then((token) => {
          settle(() => resolve(token));
        })
        .catch((error) => {
          settle(() => reject(error));
        });
    });
  }

  /**
   * Bounded registry lookup for "is this bridge already linked?". The two
   * checking-phase call sites previously awaited checkBridgeLinkedRoom
   * UNBOUNDED — a hung Lambda fetch wedged the machine in `checking` forever
   * (autoLinkBlocking → boot overlay never dismisses; CI run 27310429937,
   * win bridge-offline: no transition ever followed `checking`). Same bug
   * class getTokenWithTimeout documents. Timeout/fetch-failure degrade to
   * "not linked" — the same answer checkBridgeLinkedRoom gives for 404/403 —
   * so the machine proceeds down its normal link/recovery path instead of
   * hanging or hard-erroring on transient registry weather.
   */
  private async checkBridgeLinkedRoomBounded(
    bridgeId: Agent['id'],
    token: string
  ): Promise<RoomCheckResult & { degraded?: boolean }> {
    try {
      return await withTimeout(
        checkBridgeLinkedRoom(bridgeId, token),
        this.options.serverCheckTimeoutMs,
        'checkBridgeLinkedRoom'
      );
    } catch (error) {
      console.warn('[auto-link] linked-room server check failed/timed out:', error);
      // `degraded` marks "the registry didn't ANSWER", as opposed to the
      // definitive not-linked a 404/403 produces. Callers must not treat a
      // degraded read as proof the agent needs re-linking — re-linking an
      // already-linked agent cannot converge (linkAgentV2 participant-waits
      // on a room the agent never joins; ENG-4154 win CI runs
      // 27322897969/27326245074).
      return { alreadyLinked: false, roomId: undefined, degraded: true };
    }
  }

  /**
   * ENG-4166 D2: bounded room-accessibility check. Auto-link `success` must
   * imply the linked room is reachable under the CURRENT token — a
   * cross-account link reads the agent but 403s the room. A timeout degrades to
   * `unknown` (registry weather is never a hard cross-account verdict, exactly
   * like checkBridgeLinkedRoomBounded's degraded read), so a slow registry can
   * never fabricate a false `forbidden` that would strand a healthy boot.
   */
  private async verifyRoomAccessBounded(roomId: Room['id'], token: string): Promise<RoomAccess> {
    try {
      return await withTimeout(
        checkRoomAccessible(roomId, token),
        this.options.serverCheckTimeoutMs,
        'checkRoomAccessible'
      );
    } catch (error) {
      console.warn('[auto-link] room-access check failed/timed out:', error);
      return 'unknown';
    }
  }

  /**
   * ENG-4166 D2 terminal: the bridge is linked to a room this account cannot
   * access and cannot be relinked in place (bridge:unlink reboots; bridge:reboot
   * is suppressed under IRIS_E2E_TEST). Transition to a DISTINCT terminal error
   * instead of a false `success` — root bootstrap (D1) re-verifies after the
   * controller settles and routes this to the boot-error screen (D3), the same
   * code path in prod and e2e. Callers that created a room must clean it up
   * BEFORE calling this (the direct-return path skips the catch-block cleanup).
   */
  private transitionCrossAccountLink(agentId?: Agent['id']): void {
    autoLinkAnalytics.trackAutoLinkError({
      ...withOptionalAgentId(agentId),
      reason: 'cross_account_link',
      error_message: CROSS_ACCOUNT_LINK_MESSAGE
    });
    this.transition({
      status: 'error',
      reason: 'cross_account_link',
      message: CROSS_ACCOUNT_LINK_MESSAGE
    });
  }

  /**
   * Degraded-check fallback: when the registry could not answer, consult the
   * LOCAL identity store (sidecar truth — no network). If it reports the
   * agent linked, transition to `skipped` and do NOT re-link: local truth
   * outranks a degraded network read. A re-link of a linked agent grinds
   * through linkAgentV2's unsatisfiable participant-wait (~30s Lambda
   * timeouts), holding the boot overlay — and its pointer-events — over the
   * whole UI for minutes (the cross-platform account-settings click-timeout
   * cluster). Returns true when the machine transitioned (caller returns).
   */
  private async skipIfLocallyLinkedOnDegradedCheck(signal: AbortSignal): Promise<boolean> {
    // Bounded: resolveLinkedAgentId(false) awaits the SETTLED store with no
    // timeout of its own — under registry weather that settle can ride the
    // 30s session ceiling, and this fallback must not extend the checking
    // phase past its own window.
    const locallyLinked = await withTimeout(
      resolveLinkedAgentId(false),
      this.options.serverCheckTimeoutMs,
      'localIdentityFallback'
    ).catch(() => undefined);
    if (!locallyLinked) {
      return false;
    }
    // An abort during the local pull must not be followed by a second
    // terminal transition (abort already transitioned the machine).
    this.throwIfAborted(signal);
    console.warn(
      '[auto-link] Registry check degraded but identity store reports linked agent',
      locallyLinked,
      '— skipping re-link (local truth outranks degraded network read).'
    );
    primeMachineAgentCache(locallyLinked);
    this.transition({ status: 'skipped', reason: 'already_linked_local' });
    return true;
  }

  /**
   * Re-confirm a sidecar-reported link LIVE before trusting it over a
   * DEFINITIVE registry "no link found". The sidecar's first answer can race
   * its own boot validation — it served the stale pre-wipe link and wiped it
   * 65ms later (CI run 27332628785, win delay-amplification CV scenario), so
   * a cached answer cannot be re-trusted. The grace gives the boot validation
   * time to land; `consultSidecarLinkLive` then bypasses BOTH sticky identity
   * caches (the renderer store and the main-process resolver latch `linked`
   * for the session). Only a live `linked` confirms the skip — `unlinked` and
   * `unknown` both mean "no confirmation", sending the machine down the
   * genuine link path instead (which converges post-wipe: mac CV scenario,
   * same run, recovered via re-link in 18.4s). Returns the LIVE-confirmed
   * agent id (a fresh sidecar link may have minted a newer uuid than the
   * first answer), or undefined when unconfirmed.
   * Per `ai-code-requires-tests`: covered by the registry-disagreement suite
   * in auto-link-controller.test.ts.
   */
  private async reconfirmSidecarLinkLive(signal: AbortSignal): Promise<Agent['id'] | undefined> {
    await this.delayRespectingAbort(this.options.sidecarReconfirmGraceMs, signal);
    this.throwIfAborted(signal);
    const live = await withTimeout(
      consultSidecarLinkLive(),
      this.options.serverCheckTimeoutMs,
      'sidecarLinkReconfirm'
    ).catch(() => ({ state: 'unknown' as const }));
    this.throwIfAborted(signal);
    if (live.state !== 'linked') {
      console.warn(
        '[auto-link] Live sidecar re-consult did not confirm the link (state:',
        live.state,
        ') — the registry answer stands.'
      );
      return undefined;
    }
    return live.agentId;
  }

  /**
   * Clear a confirmed stale cross-account sidecar link (ENG-4154 warm
   * account-switch). `bridge:unlink` makes the sidecar unlink the agent in
   * the registry with its OWN stored token (account-agnostic), wipe its
   * SQLite credentials, and request an app reboot (IRIS_REBOOT_TOKEN) — the
   * restart reconverges resolver, renderer store, and the encoder still
   * sitting in the previous account's room through the fresh-install link
   * path. Deliberately NOT unlink-on-logout (memory
   * `eng4154-logout-stale-bridge-link`: that breaks same-account re-login
   * and proliferates auto-link rooms) — the unlink fires only on the
   * definitive cross-account evidence gathered by the caller. The terminal
   * `error('stale_link_cleared')` releases the boot overlay if the reboot is
   * suppressed (IRIS_E2E_TEST) or rate-limited, falling back to manual link.
   */
  private async clearStaleCrossAccountLink(
    agentId: Agent['id'],
    signal: AbortSignal
  ): Promise<void> {
    console.warn(
      '[auto-link] Sidecar re-confirmed link for agent',
      agentId,
      'but the current account neither links nor owns it — stale cross-account link (the sidecar boot validation predates the account switch). Unlinking; the sidecar reboot converges via the fresh-install link path.'
    );
    // Bounded by the IPC-class ceiling (the main-side handler is itself
    // bounded by the sidecar's 30s mailroom unlink timeout). A failed or
    // timed-out unlink still transitions below — the stale link is already
    // proven dead for this account, and manual link remains available.
    const unlinked = await withTimeout(
      ElectronAPI.sendUnlinkRequest(),
      this.options.ipcTimeoutMs,
      'staleLinkUnlink'
    ).catch((error) => {
      console.error('[auto-link] stale-link unlink request failed:', error);
      return false;
    });
    if (!unlinked) {
      console.error(
        '[auto-link] sidecar did not confirm the stale-link unlink — manual unlink/link may be required.'
      );
    }
    // ENG-4448: emitted AFTER the unlink resolves, because only then is the
    // outcome known. A CONFIRMED unlink self-heals — the sidecar's own reboot
    // re-links within ~20s (production: 4 of 8 bridge-link-failure rows were
    // this exact self-heal, each followed by a successful relink) — so it is
    // housekeeping, not a real link failure, and the row (with its `reason`)
    // still counts toward the raw, uncorrected total.
    // An UNCONFIRMED unlink is the opposite: nothing self-heals, the user is
    // stranded on manual link (see the log directly above), so it keeps
    // result:'failure'. Labelling that housekeeping would hide a real failure
    // behind the very correction this ticket exists to make.
    autoLinkAnalytics.trackAutoLinkError({
      ...withOptionalAgentId(agentId),
      reason: 'stale_link_cleared',
      error_message: 'sidecar link not owned by the current account',
      result: unlinked ? 'housekeeping' : 'failure',
      ...(unlinked ? { severity: EVENT_SEVERITY.S3_WARN } : {})
    });
    this.throwIfAborted(signal);
    this.transition({
      status: 'error',
      reason: 'stale_link_cleared',
      message: 'Cleared a bridge link belonging to a different account — relinking after restart'
    });
  }

  /** Abort-aware sleep: resolves at `ms` OR the moment the signal aborts. */
  private delayRespectingAbort(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
      if (signal.aborted || ms <= 0) {
        resolve();
        return;
      }
      const timeoutId = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(timeoutId);
        resolve();
      };
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  private async getLocalBridgeIdWithTimeout(signal: AbortSignal): Promise<Agent['id'] | undefined> {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        console.warn('[auto-link] IPC timeout');
        signal.removeEventListener('abort', onAbort);
        resolve(undefined);
      }, this.options.ipcTimeoutMs);

      const onAbort = () => {
        clearTimeout(timeoutId);
        signal.removeEventListener('abort', onAbort);
        resolve(undefined);
      };

      signal.addEventListener('abort', onAbort);

      resolveLinkedAgentId(true)
        .then((id) => {
          clearTimeout(timeoutId);
          signal.removeEventListener('abort', onAbort);
          resolve(id as Agent['id'] | undefined);
        })
        .catch((error) => {
          console.error('[auto-link] IPC error:', error);
          clearTimeout(timeoutId);
          signal.removeEventListener('abort', onAbort);
          resolve(undefined);
        });
    });
  }

  private async getLinkAgentIdsWithTimeout(
    signal: AbortSignal,
    source: Promise<LinkAgentIdsResult>
  ): Promise<LinkAgentIdsResult> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (callback: () => void): void => {
        if (settled) {
          return;
        }
        settled = true;
        signal.removeEventListener('abort', onAbort);
        clearTimeout(timeoutId);
        callback();
      };

      const timeoutId = setTimeout(() => {
        settle(() => reject(new Error('Timed out while getting bridge link details')));
      }, this.options.ipcTimeoutMs);

      const onAbort = (): void => {
        const abortError = new Error(ABORT_ERROR_MESSAGE);
        abortError.name = ABORT_ERROR_NAME;
        settle(() => reject(abortError));
      };

      signal.addEventListener('abort', onAbort);

      source
        .then((result) => {
          settle(() => resolve(result));
        })
        .catch((error) => {
          settle(() => reject(error));
        });
    });
  }
}

export const getAutoLinkController = (options?: AutoLinkControllerOptions): AutoLinkController => {
  return AutoLinkController.getInstance(options);
};
